import {
  BasketMobile,
  BundleBasketFragment,
  ProductType,
  Quote,
  ThankYouBasketFragment,
} from '@generated/gql/graphql';
import { SignupCoreServicesEntity } from '@tracking/trackingEvents';
import { discountedMobileService } from '@utils/mobile';
import { parseValueToPennies } from '@utils/price';

import { getCbcPrice } from './getCbcPrice';
import { getGroupCombinations } from './getGroupCombinations';

interface ParsedEnergy {
  monthlyCost: string;
  fuelType: string;
  energyTariffType: string;
}

interface ParsedMobile {
  amount: number;
  totalPrice: string;
}

const parseEnergyQuoteFromBasket = (
  energy: BundleBasketFragment['energy']
): Nullable<ParsedEnergy> => {
  if (!energy) return null;
  const energyQuote = energy?.selectedQuote;

  if (energyQuote?.isSelected && energyQuote.monthlyCost) {
    return {
      monthlyCost: energyQuote.monthlyCost.value,
      fuelType: energy?.fuelType,
      energyTariffType: energyQuote.name,
    };
  }

  return null;
};

const parseMobileFromBasket = (
  mobile: Nullable<BasketMobile>
): Nullable<ParsedMobile> => {
  if (!mobile || mobile.selectedSimCount === 0) return null;

  const totalPrice =
    discountedMobileService(mobile)?.total.value ??
    mobile.totalMonthlyCost.value;

  return {
    amount: mobile.selectedSimCount,
    totalPrice,
  };
};

const addEnergyService = (
  energy: Nullable<ParsedEnergy>,
  coreServices: SignupCoreServicesEntity
): void => {
  if (!energy) return;

  coreServices.energy = true;
  coreServices.energy_price = parseValueToPennies(energy.monthlyCost);
  coreServices.energy_fuel_type = energy.fuelType;
  coreServices.energy_tariff_type = energy.energyTariffType;
  coreServices.number_of_core_services++;
};

const addBroadbandService = (
  monthlyCost: string,
  coreServices: SignupCoreServicesEntity
): void => {
  coreServices.broadband = true;
  coreServices.broadband_price = parseValueToPennies(monthlyCost);
  coreServices.number_of_core_services++;
};

const addMobileService = (
  mobile: ParsedMobile,
  coreServices: SignupCoreServicesEntity
): void => {
  coreServices.mobile = true;
  coreServices.mobile_price = parseValueToPennies(mobile.totalPrice);
  coreServices.number_of_mobile_services = mobile.amount;
  coreServices.multiple_sims_selected = mobile.amount > 1;

  const additionalCoreServices = mobile.amount === 1 ? 1 : 2;
  coreServices.number_of_core_services += additionalCoreServices;
};

const addInsuranceService = (
  monthlyCost: string,
  coreServices: SignupCoreServicesEntity
): void => {
  coreServices.insurance = true;
  coreServices.insurance_price = parseValueToPennies(monthlyCost);
  coreServices.number_of_core_services++;
};

const addCashbackService = (
  monthlyCost: string,
  coreServices: SignupCoreServicesEntity
): void => {
  coreServices.cbc_selected = true;
  coreServices.cbc_price = parseValueToPennies(monthlyCost);
};

const createEmptyCoreServicesEntity = (): SignupCoreServicesEntity => ({
  broadband: false,
  broadband_price: undefined,
  energy: false,
  energy_price: undefined,
  energy_fuel_type: undefined,
  energy_tariff_type: undefined,
  mobile: false,
  mobile_price: undefined,
  multiple_sims_selected: false,
  number_of_mobile_services: 0,
  core_service_group_quantity_combinations: '',
  number_of_core_services: 0,
  total_price: 0,
  insurance: false,
  insurance_price: undefined,
  cbc_selected: false,
});

const parseCoreServicesEntity = (
  basket: Nullable<BundleBasketFragment | ThankYouBasketFragment>
): Nullable<SignupCoreServicesEntity> => {
  if (!basket) {
    return null;
  }

  const {
    selectedQuotes,
    monthlyCost: { value: totalPrice },
    energy,
    mobile,
  } = basket;

  const coreServices = createEmptyCoreServicesEntity();

  (selectedQuotes as Quote[]).forEach((quote) => {
    const {
      totalMonthlyCost,
      product: { type },
    } = quote;

    const monthlyCost = totalMonthlyCost.value;

    switch (type) {
      case ProductType.Broadband:
        addBroadbandService(monthlyCost, coreServices);
        break;

      case ProductType.Insurance:
        addInsuranceService(monthlyCost, coreServices);
        break;

      case ProductType.CashbackCard:
        addCashbackService(getCbcPrice(quote), coreServices);
        break;
    }
  });

  const parsedEnergy = parseEnergyQuoteFromBasket(energy);
  addEnergyService(parsedEnergy, coreServices);

  const parsedMobile = parseMobileFromBasket(mobile ?? null);
  if (parsedMobile) {
    addMobileService(parsedMobile, coreServices);
  }

  coreServices.total_price = parseValueToPennies(totalPrice);
  coreServices.core_service_group_quantity_combinations =
    getGroupCombinations(coreServices);

  return coreServices;
};

export { parseCoreServicesEntity };
